import { supabase } from '@/lib/supabase';
import { telegramService } from './telegramService';

interface QuoteRequestData {
  name: string;
  company: string;
  country: string;
  phone: string;
  email: string;
  model_id: string;
  model_name: string;
  message?: string;
}

interface QuoteGenerationData {
  model_id: string;
  quantity: number;
  region: string;
  company: string;
  contact_name: string;
  email: string;
  phone?: string;
  notes?: string;
}

interface GeneratedQuote {
  id: string;
  quote_number: string;
  model_id: string;
  quantity: number;
  region: string;
  base_price: number;
  unit_price: number;
  volume_discount_percentage: number;
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  total_amount: number;
  currency_code: string;
  status: string;
  valid_until: string;
  created_at: string;
}

interface PricingCalculation {
  base_price: number;
  unit_price: number;
  discount_percentage: number;
  subtotal: number;
  shipping: number;
  tax: number;
  total: number;
  currency: string;
}

export const quoteService = {
  async createQuoteRequest(data: QuoteRequestData) {
    const { error } = await supabase
      .from('quote_requests')
      .insert({
        name: data.name,
        company: data.company,
        country: data.country,
        phone: data.phone,
        email: data.email,
        model_id: data.model_id,
        model_name: data.model_name,
        message: data.message || '',
        status: 'pending'
      });

    if (error) {
      throw new Error(error.message);
    }

    // Send Telegram notification immediately after successful submission
    try {
      await telegramService.sendLeadAlert({
        leadSource: 'Quick Quote',
        productName: data.model_name,
        country: data.country,
        company: data.company,
        contactName: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message,
      });
    } catch (telegramError) {
      // Log but don't fail the submission if Telegram notification fails
      console.error('Failed to send Telegram notification:', telegramError);
    }

    return { success: true };
  },

  async getAllQuoteRequests() {
    const { data, error } = await supabase
      .from('quote_requests')
      .select(`
        *,
        models (
          name,
          category_slug,
          hero_image
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  },

  async updateStatus(id: string, status: string) {
    const { error } = await supabase
      .from('quote_requests')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      throw new Error(error.message);
    }

    return { success: true };
  },

  // DEPRECATED: Complex quote generation removed - use simple createQuoteRequest instead
  // The following methods are kept for backward compatibility with other parts of the app
  // but should not be used for the "Get Quick Quote" flow

  async generateAutomatedQuote(data: QuoteGenerationData): Promise<{ quote_id: string; quote_number: string }> {
    try {
      // First create quote request
      const { data: quoteRequest, error: requestError } = await supabase
        .from('quote_requests')
        .insert({
          name: data.contact_name,
          company: data.company,
          country: data.region,
          phone: data.phone || '',
          email: data.email,
          model_id: data.model_id,
          model_name: '', // Will be populated by trigger
          message: data.notes || '',
          status: 'pending'
        })
        .select()
        .single();

      if (requestError) throw requestError;

      // Generate quote using database function
      const { data: quoteId, error: quoteError } = await supabase
        .rpc('generate_quote', {
          p_quote_request_id: quoteRequest.id,
          p_model_id: data.model_id,
          p_quantity: data.quantity,
          p_region: data.region
        });

      if (quoteError) throw quoteError;

      // Get generated quote details
      const { data: generatedQuote, error: fetchError } = await supabase
        .from('generated_quotes')
        .select('id, quote_number')
        .eq('id', quoteId)
        .single();

      if (fetchError) throw fetchError;

      return {
        quote_id: generatedQuote.id,
        quote_number: generatedQuote.quote_number
      };
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to generate quote');
    }
  },

  async getGeneratedQuote(quoteId: string): Promise<GeneratedQuote> {
    try {
      const { data, error } = await supabase
        .from('generated_quotes')
        .select(`
          *,
          models (
            name,
            hero_image,
            category_slug
          )
        `)
        .eq('id', quoteId)
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to fetch quote');
    }
  },

  async calculatePricing(modelId: string, quantity: number, region: string): Promise<PricingCalculation> {
    try {
      // Get model pricing
      const { data: modelPrice, error: priceError } = await supabase
        .from('model_pricing')
        .select('base_price, currency_code')
        .eq('model_id', modelId)
        .lte('effective_from', new Date().toISOString())
        .or('effective_until.is.null,effective_until.gt.' + new Date().toISOString())
        .order('effective_from', { ascending: false })
        .limit(1)
        .single();

      if (priceError) throw priceError;

      // Get regional pricing
      const { data: regionalPrice, error: regionError } = await supabase
        .from('regional_pricing')
        .select('base_multiplier, shipping_rate, tax_rate, currency_code')
        .eq('region', region)
        .single();

      if (regionError) throw regionError;

      // Get volume discount
      const { data: discount, error: discountError } = await supabase
        .rpc('calculate_volume_discount', { qty: quantity });

      if (discountError) throw discountError;

      // Calculate pricing
      const basePrice = modelPrice.base_price;
      const multiplier = regionalPrice.base_multiplier;
      const discountPct = discount || 0;
      const unitPrice = basePrice * multiplier * (1 - discountPct / 100);
      const subtotal = unitPrice * quantity;
      const shipping = regionalPrice.shipping_rate * quantity;
      const tax = (subtotal + shipping) * regionalPrice.tax_rate;
      const total = subtotal + shipping + tax;

      return {
        base_price: basePrice,
        unit_price: unitPrice,
        discount_percentage: discountPct,
        subtotal,
        shipping,
        tax,
        total,
        currency: regionalPrice.currency_code
      };
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to calculate pricing');
    }
  },

  async getRegionalPricing() {
    try {
      const { data, error } = await supabase
        .from('regional_pricing')
        .select('*')
        .order('region');

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to fetch regional pricing');
    }
  },

  async getVolumeDiscounts() {
    try {
      const { data, error } = await supabase
        .from('volume_discounts')
        .select('*')
        .order('min_quantity');

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to fetch volume discounts');
    }
  },

  async approveQuote(quoteId: string, approvedBy: string) {
    try {
      const { error } = await supabase
        .from('generated_quotes')
        .update({
          status: 'approved',
          approved_by: approvedBy,
          approved_at: new Date().toISOString()
        })
        .eq('id', quoteId);

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to approve quote');
    }
  }
};
